import { useState, useEffect } from 'react'
import { validateActivityDuration } from '@/services/activities'
import { getFormattedDateString } from '@/utils/commons'
import { ACTIVITY_TYPE, OrderType, ACTIVITY_TYPE_TO_VALUE } from '@/types/Activity'
import { APPLICATION_ID, ALLOW_SAME_DATE_ACTIVITY_COUNTRIES } from '@/utils/constants/globalConstants'
import { useAppSelector, useAppDispatch } from '@/store'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@/utils/constants/routesConstants'
import { Constants } from '@/utils/constants/commonConstants'
import { errorNotificationDuration } from '@/utils/constants/activityConstants'
import { setToastNotifications } from '@/store/slices/commonSlice'

export const useActivityValidation = () => {
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState({})
  const [validationResponse, setValidationResponse] = useState(null)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const countryCode = useAppSelector(state => state.countryCode).countryCode
  const allowSameDateActivity = ALLOW_SAME_DATE_ACTIVITY_COUNTRIES.includes(countryCode)

  // Reset validation state when component unmounts
  useEffect(() => {
    return () => {
      resetValidation();
    };
  }, []);

  const createValidationPayload = (activityType, activityName, startDate, endDate, orderType) => {
    const activityTypeValue = ACTIVITY_TYPE_TO_VALUE[activityType] ?? '';
    return {
      applicationId: APPLICATION_ID[countryCode],
      name: activityTypeValue,
      title: activityName,
      activeStartDate: getFormattedDateString(startDate),
      activeEndDate: getFormattedDateString(endDate),
      ...(activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY && {
        orderType: OrderType[orderType],
      }),
    };
  };

  const handleActivityDurationValidation = async (formData) => {
    try {
      const validationPayload = createValidationPayload(
        formData.activityType,
        formData.activityName,
        formData.startDate,
        formData.endDate,
        formData.orderType
      );
      const uuid = formData?.uuid ?? null
      const response = await validateActivityDuration({...validationPayload, ...(formData.id && uuid==null && { id: formData.id }), ...(uuid && { uuid: uuid })});
      setValidationResponse(response);
      
      // Only validate for countries that are not in the allowed list for same date activities
      if (response?.success === false && !allowSameDateActivity) {
        setShowToast(true);
        setToastMessage({
          title: `${formData.activityType} already created`, 
          subTitle: "The activity type cannot be created multiple times with same attributes.",
          buttonText: "View Activity",
        });
        return false; // Validation failed
      }
      
      // Validation passed - reset any existing toast state
      resetValidation();
      return true; // Validation passed
    } catch (error) {
      // Handle API errors gracefully
      if (error?.cause) {
        dispatch(setToastNotifications({show: true, message: error.cause, type: 'error', duration: errorNotificationDuration}))
      }
      setValidationResponse(null);
      setShowToast(false);
      return false; // Return false on error
    }
  };

  const handleToastClose = () => {
    setShowToast(false)
  }

  const resetValidation = () => {
    setShowToast(false)
    setToastMessage({})
    setValidationResponse(null)
  }

  const handleViewActivity = () => {
    if (validationResponse?.success === false && validationResponse?.data?.id) {
      // Reset the modal before navigating
      setShowToast(false);
      setToastMessage({});
      setValidationResponse(null);
      const queryParam = `?review=${Constants.ReviewPublished}`;
      
      // Navigate to the activity view
      navigate(`${ROUTES.ACTIVITY_VIEW.replace(':id', validationResponse?.data?.id)}${queryParam}`);
    }
  }

  return {
    showToast,
    toastMessage,
    validationResponse,
    handleActivityDurationValidation,
    handleToastClose,
    resetValidation,
    handleViewActivity,
  }
}
